'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Pantry, Report } from '@/lib/pantryData';
import { addReport } from '@/lib/pantryData';

interface ReportFormData {
  uri: 'walang-stock' | 'hindi-na-aktibo' | 'maling-impormasyon' | 'iba-pa';
  detalye: string;
  nagulat: string;
}

interface ReportModalProps {
  pantry: Pantry;
  onClose: () => void;
}

const URI_OPTIONS = [
  { value: 'walang-stock', label: 'Ubos na ang stock' },
  { value: 'hindi-na-aktibo', label: 'Hindi na bukas' },
  { value: 'maling-impormasyon', label: 'Mali ang impormasyon' },
  { value: 'iba-pa', label: 'Iba pa' },
];

export default function ReportModal({ pantry, onClose }: ReportModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportFormData>();

  const onSubmit = async (data: ReportFormData) => {
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));

    const newReport: Report = {
      id: `rep-${Date.now()}`,
      pantryId: pantry.id,
      pantryPangalan: pantry.pangalan,
      uri: data.uri,
      detalye: data.detalye,
      nagulat: data.nagulat,
      petsa: new Date().toISOString(),
      status: 'nakabinbin',
    };
    addReport(newReport);

    setIsSubmitting(false);
    toast.success('Feedback submitted!', {
      description: 'Admin will look into it and take action. Thanks for the heads up!',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — neutral styling, no red */}
        <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle size={18} className="text-[#2d7a4f]" />
              <h3 className="font-display font-700 text-[#1e5c38] text-base">Magbigay ng Feedback</h3>
            </div>
            <p className="text-xs text-[#6b7280]">{pantry.pangalan}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#6b7280] hover:bg-white/80 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">
          {/* Uri ng Ulat */}
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1.5">
              Ano ang iyong feedback? <span className="text-[#dc2626]">*</span>
            </label>
            <select
              {...register('uri', { required: 'Please select an issue.' })}
              className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] bg-white"
            >
              <option value="">— Pumili ng isa —</option>
              {URI_OPTIONS.map((opt) => (
                <option key={`uri-${opt.value}`} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.uri && (
              <p className="mt-1 text-xs text-[#dc2626]">{errors.uri.message}</p>
            )}
          </div>

          {/* Detalye */}
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1.5">
              Ipaliwanag <span className="text-[#dc2626]">*</span>
            </label>
            <p className="text-xs text-[#6b7280] mb-1.5">Ano ang napansin mo? Makakatulong ito sa admin.</p>
            <textarea
              {...register('detalye', {
                required: 'Please add some details.',
                minLength: { value: 20, message: 'Add a bit more — at least 20 characters.' },
              })}
              rows={3}
              placeholder="hal. Pumunta ako kahapon at wala nang pagkain..."
              className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] resize-none"
            />
            {errors.detalye && (
              <p className="mt-1 text-xs text-[#dc2626]">{errors.detalye.message}</p>
            )}
          </div>

          {/* Pangalan */}
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1.5">
              Pangalan mo <span className="text-[#dc2626]">*</span>
            </label>
            <input
              type="text"
              {...register('nagulat', { required: 'Please enter your name.' })}
              placeholder="Juan dela Cruz"
              className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]"
            />
            {errors.nagulat && (
              <p className="mt-1 text-xs text-[#dc2626]">{errors.nagulat.message}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-sm font-medium text-[#6b7280] border border-[#d1d5db] rounded-xl hover:bg-[#f9fafb] transition-colors"
            >
              Kanselahin
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold bg-[#2d7a4f] hover:bg-[#1e5c38] active:scale-95 text-white rounded-xl transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Nagsu-submit…
                </>
              ) : (
                'I-submit ang Feedback'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}